
import { useState } from 'react';
import AdminSidebar from '../components/AdminSidebar';
import AdminHeader from '../components/AdminHeader';
import ProductsTable from './components/ProductsTable';
import AddProductModal from './components/AddProductModal';
import EditProductModal from './components/EditProductModal';
import DeleteConfirmModal from './components/DeleteConfirmModal';
import { createProduct, deleteProduct, getCategories, getProducts, updateProduct } from '../../../api/apiServices';

export default function AdminProductsPage() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [deletingProduct, setDeletingProduct] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);

  const filteredProducts = products.filter((p) =>
  p.name.toLowerCase().includes(searchQuery.toLowerCase())
);

useEffect(() => {
  const fetchProducts = async () => {
    try {
      const response = await getProducts();
      setProducts(response);
    } catch (error) {
      console.error("Failed to load products:", error);
    }
  };

  fetchProducts();
}, []);

  useEffect(() => {
  const fetchCategories = async () => {
    try {
      setLoadingCategories(true);
      const response = await getCategories();

      setCategories(response.data); 

    } catch (error) {
      console.error("Failed to load categories:", error);
    } finally {
      setLoadingCategories(false);
    }
  };

  fetchCategories();
}, []);


  const handleAddProduct = async (formData) => {
  try {
    const response = await createProduct(formData);

    const newProduct = response.data;

    setProducts((prev) => [...prev, newProduct]);

    setIsAddModalOpen(false);
  } catch (error) {
    console.error("Failed to add product:", error);
  }
};


  const handleEditProduct = async (id, formData) => {
  try {
    const response = await updateProduct(id, formData);
    const updatedProduct = response.data;

    setProducts((prev) =>
      prev.map((p) => (p._id === id ? updatedProduct : p))
    );

    setEditingProduct(null);
  } catch (error) {
    console.error("Failed to update product:", error);
  }
};

const handleDeleteProduct = async () => {
  try {
    if (!deletingProduct) return;

    await deleteProduct(deletingProduct._id);

    setProducts((prev) =>
      prev.filter((c) => c._id !== deletingProduct._id)
    );

    setSelectedProducts([]);
    setDeletingProduct(null);
  } catch (error) {
    console.error("Failed to delete product:", error);
  }
};


  const handleBulkDelete = () => {
    setProducts(products.filter((p) => !selectedProducts.includes(p._id)));
    setSelectedProducts([]);
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedProducts(filteredProducts.map((p) => p._id));
    } else {
      setSelectedProducts([]);
    }
  };

  const handleSelectProduct = (id, checked) => {
    if (checked) {
      setSelectedProducts([...selectedProducts, id]);
    } else {
      setSelectedProducts(selectedProducts.filter((pid) => pid !== id));
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] font-['Poppins']">
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        ></div>
      )}

      <AdminSidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <div className="lg:ml-64 min-h-screen">
        <AdminHeader onMenuClick={() => setIsSidebarOpen(true)} />

        <main className="p-4 md:p-6 lg:p-8">
          <div className="mb-6">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Products Management</h1>
            <p className="text-sm text-gray-500">Manage your Best Sellers and New Arrivals products</p>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-4 md:p-6 border-b border-gray-200">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">

                <div className="flex items-center gap-3">
                  <div className="relative flex-1 md:flex-initial">
                    <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg"></i>
                    <input
                      type="text"
                      placeholder="Search products..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full md:w-64 pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#EF579A] focus:border-transparent"
                    />
                  </div>

                  <button
                    onClick={() => setIsAddModalOpen(true)}
                    className="px-4 py-2 bg-[#EF579A] text-white rounded-lg text-sm font-medium hover:bg-[#D94A85] transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-add-line text-lg"></i>
                    <span className="hidden sm:inline">Add Product</span>
                  </button>
                </div>
              </div>

              {selectedProducts.length > 0 && (
                <div className="mt-4 flex items-center justify-between bg-[#FFF5F9] border border-[#EF579A]/20 rounded-lg p-3">
                  <span className="text-sm text-gray-700">
                    {selectedProducts.length} product{selectedProducts.length > 1 ? 's' : ''} selected
                  </span>
                  <button
                    onClick={handleBulkDelete}
                    className="px-4 py-1.5 bg-red-500 text-white rounded-md text-sm font-medium hover:bg-red-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Delete Selected
                  </button>
                </div>
              )}
            </div>

            <ProductsTable
              products={filteredProducts}
              selectedProducts={selectedProducts}
              onSelectAll={handleSelectAll}
              onSelectProduct={handleSelectProduct}
              onEdit={setEditingProduct}
              onDelete={setDeletingProduct}
            />
          </div>
        </main>
      </div>

      {isAddModalOpen && (
        <AddProductModal
          categories={categories}
          onClose={() => setIsAddModalOpen(false)}
          onAdd={handleAddProduct}
        />
      )}

      {editingProduct && (
        <EditProductModal
          product={editingProduct}
          categories={categories}
          onClose={() => setEditingProduct(null)}
          onSave={handleEditProduct}
        />
      )}

      {deletingProduct && (
        <DeleteConfirmModal
          productName={deletingProduct.name}
          onClose={() => setDeletingProduct(null)}
          onConfirm={() => handleDeleteProduct(deletingProduct._id)}
        />
      )}
    </div>
  );
}
